import type { AdminViewStatsDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsTile } from './AnalyticsTile';
import type { ViewsTileProps } from './ViewsTopRow';
import { tokens } from '../../../design';
import { axisTicks, tickFraction } from '../_lib/axisTicks';
import { buildSeriesPath } from '../_lib/chartGeometry';
import { formatViews } from '../_lib/formatViews';

/** Tile «Просмотры»: two totals + a two-line per-day chart (hand-built SVG, as GMV). */

const VB_W = 1200;
const VB_H = 120;

const Total = ({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}): ReactElement => (
  <Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        fontSize: 12,
        color: tokens.text.secondary,
        mb: 0.75,
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: 999, background: color }} />
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: tokens.font.mono,
        fontSize: 30,
        fontWeight: 700,
        letterSpacing: '-0.6px',
        lineHeight: 1,
      }}
    >
      {formatViews(value)}
    </Box>
  </Box>
);

const ViewsChart = ({
  days,
  label,
}: {
  days: AdminViewStatsDto['days'];
  label: string;
}): ReactElement => {
  const max = days.reduce((m, d) => Math.max(m, d.listings, d.profiles), 0);
  const listings = buildSeriesPath(
    days.map((d) => d.listings),
    VB_W,
    VB_H,
    max,
  );
  const profiles = buildSeriesPath(
    days.map((d) => d.profiles),
    VB_W,
    VB_H,
    max,
  );
  const { t } = useTranslation();
  const ticks = axisTicks(days.map((d) => d.date));
  return (
    <>
      <Box
        component="svg"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={label}
        sx={{ mt: 2, width: '100%', height: 100, display: 'block' }}
      >
        <path
          d={profiles.line}
          fill="none"
          stroke={tokens.state.info}
          strokeWidth="2"
          opacity="0.85"
        />
        <path d={listings.line} fill="none" stroke={tokens.brand.primary} strokeWidth="3" />
      </Box>
      <Box
        sx={{
          position: 'relative',
          height: 16,
          fontSize: 11,
          color: tokens.text.tertiary,
          mt: 0.5,
        }}
      >
        {ticks.map((tick) => {
          const fraction = tickFraction(tick.index, days.length);
          const shift = fraction <= 0 ? '0' : fraction >= 1 ? '-100%' : '-50%';
          return (
            <Box
              component="span"
              key={tick.index}
              sx={{
                position: 'absolute',
                left: `${fraction * 100}%`,
                transform: `translateX(${shift})`,
                whiteSpace: 'nowrap',
              }}
            >
              {tick.label ?? t('dashboard.views.today')}
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export const ViewsTotalsTile = ({
  data,
  isLoading,
  isError,
  gridColumn,
}: ViewsTileProps): ReactElement => {
  const { t } = useTranslation();
  const label = t('dashboard.views.title');
  return (
    <AnalyticsTile
      label={label}
      gridColumn={gridColumn}
      isLoading={isLoading}
      isError={isError}
      hasData={Boolean(data)}
    >
      {data ? (
        <>
          <Box sx={{ display: 'flex', gap: 6 }}>
            <Total
              color={tokens.brand.primary}
              label={t('dashboard.views.listings')}
              value={data.totals.listings}
            />
            <Total
              color={tokens.state.info}
              label={t('dashboard.views.profiles')}
              value={data.totals.profiles}
            />
          </Box>
          <ViewsChart days={data.days} label={label} />
        </>
      ) : null}
    </AnalyticsTile>
  );
};
